import { devLog, devError } from '../utils/devLogger';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Table, Button, Badge, Navbar, Nav, Alert, Spinner } from 'react-bootstrap';
import { useAuth } from '../auth/AuthContext';
import { db, auth } from '../firebase';
import { doc, getDoc, collection, getDocs, query, where } from 'firebase/firestore';

const UserDetailsPage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [user, setUser] = useState(null);
  const [userProgress, setUserProgress] = useState(null);
  const [enrolledModules, setEnrolledModules] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (userId) {
      loadUserDetails();
    }
  }, [userId]);

  const loadUserDetails = async () => {
    try {
      setLoading(true);

      // Fetch user document
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (!userDoc.exists()) {
        setUser(null);
        return;
      }

      const userData = { uid: userDoc.id, ...userDoc.data() };
      setUser(userData);

      // Get user progress if they're a learner
      if (userData.role === 'learner') {
        const progress = {
          enrolledModules: userData.enrolledModules || [],
          completedModules: userData.completedModules || [],
          completionDates: userData.completionDates || {},
          moduleProgress: userData.contentProgress || {}
        };
        setUserProgress(progress);

        // Fetch all modules to get details
        if (progress.enrolledModules.length > 0) {
          const modulesSnapshot = await getDocs(collection(db, 'modules'));
          const allModules = modulesSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));

          // Get enrolled modules details
          const enrolledModuleDetails = progress.enrolledModules.map(moduleId => {
            const module = allModules.find(m => m.id === moduleId);
            const isCompleted = progress.completedModules.includes(moduleId);
            return module ? {
              ...module,
              isCompleted,
              completionDate: isCompleted ? progress.completionDates?.[moduleId] : null
            } : null;
          }).filter(Boolean);

          setEnrolledModules(enrolledModuleDetails);
        }
      }
    } catch (error) {
      devError('Error loading user details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await auth.signOut();
      navigate('/login');
    } catch (error) {
      devError('Failed to log out', error);
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin': return 'danger';
      case 'facilitator': return 'warning';
      case 'learner': return 'primary';
      default: return 'secondary';
    }
  };

  const getStatusBadgeColor = (status) => {
    return status === 'active' ? 'success' : 'secondary';
  };

  const getProgressPercentage = () => {
    if (!userProgress || !userProgress.enrolledModules.length) return 0;
    return Math.round((userProgress.completedModules.length / userProgress.enrolledModules.length) * 100);
  };

  if (currentUser?.role !== 'admin') {
    return (
      <Container className="mt-5">
        <Alert variant="danger">
          <h4>Access Denied</h4>
          <p>You do not have permission to view user details.</p>
          <Button variant="primary" onClick={() => {
            if (currentUser?.role === 'facilitator') {
              navigate('/facilitator');
            } else {
              navigate('/dashboard');
            }
          }}>
            Go to Dashboard
          </Button>
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container className="mt-5">
        <div className="text-center py-5">
          <Spinner animation="border" role="status" variant="primary">
            <span className="visually-hidden">Loading user details...</span>
          </Spinner>
          <p className="mt-3 text-muted">Loading user details...</p>
        </div>
      </Container>
    );
  }

  if (!user) {
    return (
      <Container className="mt-5">
        <Alert variant="warning">
          <h4>User Not Found</h4>
          <p>The requested user could not be found.</p>
          <Button variant="primary" onClick={() => navigate('/admin/users')}>
            Back to User Management
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <>
      <Navbar bg="dark" variant="dark" expand="lg">
        <Container>
          <Navbar.Brand>Access70 LMS - User Details</Navbar.Brand>
          <Navbar.Toggle aria-controls="basic-navbar-nav" />
          <Navbar.Collapse id="basic-navbar-nav">
            <Nav className="ms-auto">
              <Nav.Link onClick={() => navigate('/admin')}>Admin Dashboard</Nav.Link>
              <Nav.Link onClick={() => navigate('/admin/users')}>User Management</Nav.Link>
              <Nav.Link onClick={() => navigate('/admin/reports')}>Reports</Nav.Link>
              <Nav.Link disabled>Welcome, {currentUser?.displayName || currentUser?.email}</Nav.Link>
              <Button variant="outline-light" onClick={handleLogout}>Logout</Button>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      <Container className="mt-4">
        <Row className="mb-4">
          <Col>
            <Button variant="outline-secondary" onClick={() => navigate('/admin/users')}>
              ← Back to User Management
            </Button>
          </Col>
        </Row>

        {/* User Information Card */}
        <Row className="mb-4">
          <Col>
            <Card>
              <Card.Header>
                <h3>User Information</h3>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={2}>
                    <div 
                      className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center mx-auto"
                      style={{ width: '80px', height: '80px', fontSize: '32px' }}
                    >
                      {user.displayName.charAt(0)}
                    </div>
                  </Col>
                  <Col md={10}>
                    <h4>{user.displayName}</h4>
                    <p className="text-muted mb-2">{user.email}</p>
                    <div className="mb-2">
                      <Badge bg={getRoleBadgeColor(user.role)} className="me-2">
                        {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                      </Badge>
                      <Badge bg={getStatusBadgeColor(user.status)}>
                        {user.status.charAt(0).toUpperCase() + user.status.slice(1)}
                      </Badge>
                    </div>
                    <p><strong>Joined:</strong> {new Date(user.enrolledDate).toLocaleDateString()}</p>
                    <p><strong>User ID:</strong> {user.uid}</p>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Learning Progress (only for learners) */}
        {user.role === 'learner' && userProgress && (
          <Row className="mb-4">
            <Col>
              <Card>
                <Card.Header>
                  <h4>Learning Progress</h4>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={3}>
                      <div className="text-center">
                        <h5>Enrolled Modules</h5>
                        <div className="display-4 text-primary">{userProgress.enrolledModules.length}</div>
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="text-center">
                        <h5>Completed Modules</h5>
                        <div className="display-4 text-success">{userProgress.completedModules.length}</div>
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="text-center">
                        <h5>Overall Progress</h5>
                        <div className="display-4 text-info">{getProgressPercentage()}%</div>
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="text-center">
                        <h5>Status</h5>
                        <div className="mt-2">
                          {getProgressPercentage() === 100 ? (
                            <Badge bg="success" className="p-2">Completed</Badge>
                          ) : getProgressPercentage() > 0 ? (
                            <Badge bg="warning" className="p-2">In Progress</Badge>
                          ) : (
                            <Badge bg="secondary" className="p-2">Not Started</Badge>
                          )}
                        </div>
                      </div>
                    </Col>
                  </Row>
                  <Row className="mt-4">
                    <Col>
                      <div className="progress" style={{ height: '20px' }}>
                        <div 
                          className="progress-bar" 
                          role="progressbar" 
                          style={{ width: `${getProgressPercentage()}%` }}
                          aria-valuenow={getProgressPercentage()}
                          aria-valuemin="0"
                          aria-valuemax="100"
                        >
                          {getProgressPercentage()}%
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {/* Enrolled Modules (only for learners) */}
        {user.role === 'learner' && enrolledModules.length > 0 && (
          <Row className="mb-4">
            <Col>
              <Card>
                <Card.Header>
                  <h4>Enrolled Modules</h4>
                </Card.Header>
                <Card.Body>
                  <Table striped bordered hover responsive>
                    <thead>
                      <tr>
                        <th>Module Name</th>
                        <th>Description</th>
                        <th>Duration</th>
                        <th>Status</th>
                        <th>Completion Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {enrolledModules.map((module) => (
                        <tr key={module.id}>
                          <td><strong>{module.name}</strong></td>
                          <td>{module.description}</td>
                          <td>{module.duration}</td>
                          <td>
                            <Badge bg={module.isCompleted ? 'success' : 'warning'}>
                              {module.isCompleted ? 'Completed' : 'In Progress'}
                            </Badge>
                          </td>
                          <td>
                            {module.completionDate ? 
                              new Date(module.completionDate).toLocaleDateString() : 
                              'Not completed'
                            }
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {/* System Information */}
        <Row className="mb-4">
          <Col>
            <Card>
              <Card.Header>
                <h4>System Information</h4>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <p><strong>Account Created:</strong> {new Date(user.enrolledDate).toLocaleDateString()}</p>
                    <p><strong>Last Login:</strong> {user.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : 'Never'}</p>
                  </Col>
                  <Col md={6}>
                    <p><strong>Account Status:</strong> {user.status}</p>
                    <p><strong>Role:</strong> {user.role}</p>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default UserDetailsPage;